<template>
  <div>
    <b-modal
      id="create-peak-factors"
      ok-only
      ok-title="Done"
      centered
      ok-variant="success"
      size="xl"
      title="Add Peak Factors"
      :ok-disabled="validateFactors"
      @ok="$emit('peak-factors', factors)"
    >
      <b-card
        v-for="(peakFactor , index) in peakFactors"
        :key="index"
        no-body
        class="p-1"
      >
        <b-row>
          <b-col md="3">
            <b-form-group
              label="Name"
              label-for="peak-factors-name"
            >
              <b-form-input
                id="peak-factors-name"
                v-model="peakFactor.name"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              label="Factor"
              label-for="peak-factors-factor"
            >
              <b-form-input
                id="peak-factors-factor"
                v-model.number="peakFactor.factor"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            class="d-flex align-items-center"
          >
            <b-button
              variant="success"
              @click="pushNewRule(peakFactor)"
            >
              New Rule
            </b-button>
          </b-col>
          <b-col
            md="3"
            class="d-flex align-items-center"
          >
            <b-button
              variant="primary"
              @click="deletePeakFactor(index)"
            >
              Delete
            </b-button>
          </b-col>
        </b-row>
        <b-card
          v-for="(rule,ruleIndex) in peakFactor.config.rules"
          :key="ruleIndex"
          no-body
        >
          <h5>Rule {{ ruleIndex + 1 }}</h5>
          <b-row class="border-secondary rounded py-1">
            <b-col md="4">
              <b-form-group
                v-slot="{ ariaDescribedby }"
                label="Days"
              >
                <b-form-checkbox-group
                  :id="`peak-factors-byday-${ruleIndex}-${index}`"
                  v-model="rule.selectedDays"
                  :options="days"
                  :aria-describedby="ariaDescribedby"
                  :name="`peak-factors-byday-${ruleIndex}-${index}`"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Date">
                <flat-pickr
                  v-model="rule.dtstart"
                  class="form-control"
                />
              </b-form-group>
            </b-col>
            <b-col md="1">
              <b-form-group label="From (H)">
                <flat-pickr
                  v-model="rule.from_hour"
                  class="form-control"
                  :config="{ enableTime: true, noCalendar: true, dateFormat: 'H:i',}"
                />

              </b-form-group>
            </b-col>
            <b-col md="1">
              <b-form-group label="To (H)">
                <flat-pickr
                  v-model="rule.to_hour"
                  class="form-control"
                  :config="{ enableTime: true, noCalendar: true, dateFormat: 'H:i',}"
                />

              </b-form-group>
            </b-col>
            <b-col
              md="2"
              class="d-flex justify-content-between align-items-center"
            >
              <b-button
                variant="primary"
                size="sm"
                @click="deletePeakFactorRule(peakFactor.config.rules, ruleIndex)"
              >
                Delete
              </b-button>
              <b-button
                :class="{'scale-button':rule.selectedDays && rule.selectedDays.length !=0
                  && rule.dtstart
                  && rule.from_hour
                  && rule.to_hour
                  && !rule.text}"
                variant="success"
                size="sm"
                @click="createPeakFactorRule(rule)"
              >
                Save
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-card>
      <div class="d-flex justify-content-between">
        <b-button
          variant="success"
          @click="pushNewPeakFactor = true"
        >
          New Peak Factor
        </b-button>
        <span
          v-if="validateFactors"
          class="text-danger"
        >Please Fill out the empty fields</span>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, BFormGroup, BFormInput, BFormCheckboxGroup, BRow, BCol, BButton, BCard,
} from 'bootstrap-vue'
import { computed, ref, watch } from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import { RRule } from 'rrule'

export default {
  components: {
    BCard,
    BModal,
    BFormGroup,
    BFormInput,
    BFormCheckboxGroup,
    flatPickr,
    BRow,
    BCol,
    BButton,
  },
  props: {
    selectedPeakFactors: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const days = [
      { text: 'Satuday', value: RRule.SA },
      { text: 'Sunday', value: RRule.SU },
      { text: 'Monday', value: RRule.MO },
      { text: 'Tuesday', value: RRule.TU },
      { text: 'Wednesday', value: RRule.WE },
      { text: 'Thusday', value: RRule.TH },
      { text: 'Friday', value: RRule.FR },
    ]
    const pushNewPeakFactor = ref(false)
    const factors = ref([])

    watch(() => props.selectedPeakFactors, () => {
      factors.value = props.selectedPeakFactors
      factors.value.forEach(fct => {
        fct.config.rules.forEach(r => {
          if (r.text) {
            const splitByDtStart = r.text.split(';')[0]
            let dtStart = splitByDtStart.substring(
              splitByDtStart.indexOf(':') + 1,
              splitByDtStart.lastIndexOf('\n'),
            )
            for (let i = 0; i < dtStart.length - 1; i += 1) {
              // eslint-disable-next-line no-param-reassign
              if (i === 4 || i === 7) dtStart = `${dtStart.substring(0, i)}-${dtStart.substring(i, dtStart.length)}`
              if (i === 13 || i === 16 || i === 20) dtStart = `${dtStart.substring(0, i)}:${dtStart.substring(i, dtStart.length)}`
            }
            // eslint-disable-next-line no-param-reassign
            r.dtstart = new Date(dtStart)
            // eslint-disable-next-line no-param-reassign
            r.selectedDays = []
            const splitByComma = r.text.split(';')
            const splitByDay = splitByComma[2].split('BYDAY=')
            const splitDays = splitByDay[1].split(',')
            splitDays.forEach(d => {
              r.selectedDays.push(RRule[d])
            })
          }
        })
      })
    })
    const peakFactors = computed(() => {
      if (pushNewPeakFactor.value) {
        const peakFactor = {
          name: null,
          factor: null,
          config: {
            rules: [
              {
                selectedDays: [],
                text: null,
              },
            ],
          },
        }
        factors.value.push(peakFactor)
        pushNewPeakFactor.value = false
      }
      return factors.value
    })

    const validateFactors = computed(() => factors.value.some(fctr => {
      if (!fctr.name || !fctr.factor || fctr.config.rules.some(rule => rule.text == null)) {
        return true
      }
      return false
    }))

    const deletePeakFactor = index => {
      factors.value.splice(index, 1)
    }

    const pushNewRule = peakFactor => {
      peakFactor.config.rules.push({
        selectedDays: [],
        text: null,
      })
    }
    const createPeakFactorRule = rule => {
      const generatedRule = new RRule({
        freq: RRule.DAILY,
        interval: 1,
        byweekday: rule.selectedDays,
        dtstart: new Date(rule.dtstart),
      })
      // eslint-disable-next-line no-param-reassign
      rule.text = generatedRule.toString()
    }
    const deletePeakFactorRule = (peakFactorRules, ruleIndex) => {
      peakFactorRules.splice(ruleIndex, 1)
    }

    return {
      days,
      pushNewPeakFactor,
      peakFactors,
      factors,
      deletePeakFactor,
      deletePeakFactorRule,
      createPeakFactorRule,
      pushNewRule,
      validateFactors,
    }
  },
}
</script>

<style scoped lang="scss">
.scale-button {
  animation: pulse .5s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite alternate ;
}
@keyframes pulse {
  from {
    transform: scale(1)
  }
  to {
  transform: scale(1.2)
  }
}
</style>
